import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'

export interface PlanetMeta {
  id: string
  name: string
  galaxy: number
  system: number
  position: number
  is_homeworld: boolean
}

interface PlanetContextValue {
  selectedPlanetId: string | null
  setSelectedPlanetId: (id: string) => void
  planets: PlanetMeta[]
  loadingPlanets: boolean
}

const PlanetContext = createContext<PlanetContextValue>({
  selectedPlanetId: null,
  setSelectedPlanetId: () => {},
  planets: [],
  loadingPlanets: true,
})

export function PlanetProvider({ children }: { children: ReactNode }) {
  const [planets, setPlanets] = useState<PlanetMeta[]>([])
  const [selectedPlanetId, setSelectedPlanetId] = useState<string | null>(null)
  const [loadingPlanets, setLoadingPlanets] = useState(true)

  useEffect(() => {
    let userId: string | null = null

    async function load() {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      userId = user.id
      const { data } = await supabase
        .from('planets')
        .select('id, name, galaxy, system, position, is_homeworld')
        .eq('user_id', user.id)
        .order('is_homeworld', { ascending: false })
      if (data && data.length > 0) {
        setPlanets(data as PlanetMeta[])
        const hw = data.find(p => p.is_homeworld) ?? data[0]
        setSelectedPlanetId(hw.id)
      }
      setLoadingPlanets(false)
    }

    async function reload() {
      if (!userId) return
      const { data } = await supabase
        .from('planets')
        .select('id, name, galaxy, system, position, is_homeworld')
        .eq('user_id', userId)
        .order('is_homeworld', { ascending: false })
      if (data) {
        setPlanets(prev => {
          // Select new planet automatically if a new one appeared
          const newPlanet = data.find(p => !prev.some(pp => pp.id === p.id))
          if (newPlanet) setSelectedPlanetId(newPlanet.id)
          return data as PlanetMeta[]
        })
      }
    }

    load()

    const channel = supabase
      .channel('planet-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'planets' }, reload)
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [])

  return (
    <PlanetContext.Provider value={{ selectedPlanetId, setSelectedPlanetId, planets, loadingPlanets }}>
      {children}
    </PlanetContext.Provider>
  )
}

export function usePlanetContext() {
  return useContext(PlanetContext)
}
